import { lazy } from "react"
import type { ComponentType } from "react"

const RELOAD_KEY = "staleChunkReloadAt"
const RELOAD_WINDOW_MS = 10_000

// Browsers report a missing chunk with a plain Error, so the message is all we
// have to go on. Chrome, Firefox and Safari each word it differently, and the
// MIME one comes from the SPA fallback answering with index.html.
const STALE_CHUNK_MESSAGES = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "expected a javascript-or-wasm module script",
]

export const isStaleChunkError = (error: unknown): boolean => {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase()
  return STALE_CHUNK_MESSAGES.some((candidate) => message.includes(candidate))
}

const readLastReload = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0
  } catch {
    return 0
  }
}

const markReload = (at: number): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(at))
    return true
  } catch {
    return false
  }
}

/**
 * Reloads the page so it picks up a fresh index.html with the current chunk
 * hashes. Guarded by a timestamp: without a way to record that we already
 * reloaded, a genuinely broken build would reload forever, so we show the error
 * instead.
 */
const justReloaded = (): boolean => Date.now() - readLastReload() < RELOAD_WINDOW_MS

export const reloadOnceForStaleChunk = (): boolean => {
  if (justReloaded()) return false
  if (!markReload(Date.now())) return false

  window.location.reload()
  return true
}

/**
 * Keeps the Suspense fallback up while a reload is in flight, instead of
 * flashing the error boundary on the way out.
 */
const hangUntilReload = (): Promise<never> => new Promise(() => {})

/**
 * `lazy` that recovers from a chunk left behind by a deploy. Anything else is
 * rethrown to the error boundary.
 */
export const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) =>
  lazy(() =>
    factory().then(
      // In a build the factory is vite's `__vitePreload`: once the listener
      // below preventDefault()s, it swallows the error and resolves with
      // `undefined`. With no reload behind it, a missing `default` is a real
      // problem, so React gets to report it.
      (module) => (!module?.default && justReloaded() ? hangUntilReload() : module),
      (error: unknown) => {
        if (isStaleChunkError(error) && reloadOnceForStaleChunk()) {
          return hangUntilReload()
        }
        throw error
      },
    ),
  )

/**
 * Recovers from a modulepreload for a chunk a deploy removed. In a build this
 * also catches the failures of the lazy imports themselves, which is why
 * `lazyWithReload` has to cope with vite resolving them as `undefined`.
 */
export const initStaleChunkRecovery = (): (() => void) => {
  const onPreloadError = (event: Event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault()
  }

  window.addEventListener("vite:preloadError", onPreloadError)
  return () => window.removeEventListener("vite:preloadError", onPreloadError)
}
